import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { useAppMutation } from '../use-app-mutation.hook';

/** The contractual position of one reverse-vested issuance, before any acceleration. */
export type ReverseVestingTerminationPreviewRow = {
  /** Unvested shares still held: the ceiling for the Accelerated field. */
  contractualUnvestedShares: number;
  /** Vested shares still held. */
  contractualVestedShares: number;
  /** Par value on an incorporation row, share price on an issuance row. */
  defaultRepurchasePrice: number;
  /**
   * Schedule shares no longer held (buyback, secondary, class conversion), attributed vested-first:
   * `reverseVestingShares` − `contractualVestedShares` − `contractualUnvestedShares`.
   */
  disposedShares: number;
  fullVestingDate: string;
  /** The issuance / incorporation item id, which is how the terminate payload names the row. */
  id: string;
  issueDate: string;
  /** Shares subject to reverse vesting — not the row's total issued shares. */
  reverseVestingShares: number;
  shareClassName: string;
  valuationName: null | string;
};

export type PreviewReverseVestingTerminationResponse = {
  rows: ReverseVestingTerminationPreviewRow[];
};

type PreviewReverseVestingTerminationPayload = {
  companyId: string;
  data: { isLastVestingDateManual?: boolean; lastVestingDate?: string; terminationDate: string };
  stakeholderId: string;
};

/**
 * A mutation rather than a query because the contractual position depends on the Last Vesting Date
 * the operator is still editing — the same reason `usePredictTerminationVesting` is a mutation.
 * Nothing is persisted by this call.
 */
export const usePreviewReverseVestingTermination = () => {
  const { mutate, isPending } = useAppMutation<
    PreviewReverseVestingTerminationResponse,
    HttpException,
    PreviewReverseVestingTerminationPayload
  >([QueryKey.PREVIEW_REVERSE_VESTING_TERMINATION], {
    mutationFn: ({ companyId, stakeholderId, data }: PreviewReverseVestingTerminationPayload) =>
      ApiService.post(
        {
          endpoint: BackendRoute.COMPANIES,
          routePath: [
            companyId,
            'stakeholders',
            stakeholderId,
            'preview-reverse-vesting-termination',
          ],
        },
        { body: data },
      ),
  });

  return { previewReverseVestingTermination: mutate, isPending };
};
